import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;
  /**
  * Minimum scroll delta before updating direction (layout jitter guard)
  * @constant {number}
  */
  #scrollThreshold = 5;

  /**
   * Last reported header height (avoid redundant CSS var updates)
   * @type {number}
  */
  #lastReportedHeaderHeight = 0;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);

    if (roundedHeaderHeight !== this.#lastReportedHeaderHeight) {
      this.#lastReportedHeaderHeight = roundedHeaderHeight;
      document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    }

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        const nextState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.stickyState !== nextState) {
          this.dataset.stickyState = nextState;
        }
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting;
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Sets data-scroll-direction only when the value changes.
   * @param {'none' | 'up' | 'down'} value
  */
  #setScrollDirection(value) {
    if (this.dataset.scrollDirection !== value) {
      this.dataset.scrollDirection = value;
    }
  }

  /**
   * Sets data-sticky-state only when the value changes.
   * @param {'inactive' | 'active' | 'idle'} value
  */
  #setStickyState(value) {
    if (this.dataset.stickyState !== value) {
      this.dataset.stickyState = value;
    }
  }

  #initScrollBaseline() {
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode !== 'scroll-up') return;

    const scrollTop = this.#lastScrollTop;
    const headerHeight = this.offsetHeight || 0;

    if (scrollTop > headerHeight + this.#scrollThreshold) {
      this.#offscreen = true;
      this.#setStickyState('idle');
      this.#setScrollDirection('none');
    } else {
      this.#offscreen = false;
      this.#setStickyState('inactive');
      this.#setScrollDirection('none');
    }
  }

  #handlePageShow = () => {
    this.#initScrollBaseline();
    requestAnimationFrame(() => this.#initScrollBaseline());
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const delta = scrollTop - this.#lastScrollTop;

    // scroll-up: chỉ xử lý khi đã qua header; always: luôn xử lý
    if (!this.#offscreen && stickyMode !== 'always') return;

    // B: bỏ qua jitter layout / scroll ảo
    if (Math.abs(delta) < this.#scrollThreshold) {
      return;
    }

    const isScrollingUp = delta < 0;
    const isAtTop = scrollTop <= this.#scrollThreshold;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.#setScrollDirection('none');
      } else if (isScrollingUp) {
        this.#setScrollDirection('up');
      } else {
        this.#setScrollDirection('down');
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        this.#offscreen = false;
        this.#setStickyState('inactive');
        this.#setScrollDirection('none');
      } else {
        this.#setStickyState('active');
        this.#setScrollDirection('up');
      }
    } else if (this.dataset.stickyState === 'active') {
      this.#setScrollDirection('none');
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.#setStickyState('idle');
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.#setScrollDirection('none');
      this.#setStickyState('idle');
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
        window.addEventListener('pageshow', this.#handlePageShow);
        this.#initScrollBaseline();
        requestAnimationFrame(() => this.#initScrollBaseline());
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('pageshow', this.#handlePageShow);

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!headerGroup) return;

  let lastReportedHeaderGroupHeight = 0;

  const resizeObserver = new ResizeObserver((entries) => {
    const headerGroupHeight = entries.reduce((totalHeight, entry) => {
      if (
        entry.target !== header ||
        (header?.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
      ) {
        return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
      }
      return totalHeight;
    }, 0);

    const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
    if (roundedHeaderGroupHeight === lastReportedHeaderGroupHeight) return;
    lastReportedHeaderGroupHeight = roundedHeaderGroupHeight;

    document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
  });

  if (header instanceof HTMLElement) {
    resizeObserver.observe(header);
  }

  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element instanceof HTMLElement) {
      resizeObserver.observe(element);
    }
  }

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        const children = headerGroup.children;
        for (let i = 0; i < children.length; i++) {
          const element = children[i];
          if (element instanceof HTMLElement) {
            resizeObserver.observe(element);
          }
        }
      }
    }
  });

  mutationObserver.observe(headerGroup, { childList: true });
});
